import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/dateFormatter';
import './AdminFranchises.css';

const AdminFranchises = () => {
    const navigate = useNavigate();
    const [subTab, setSubTab] = useState('pending');
    const [pendingRequests, setPendingRequests] = useState([]);
    const [activeFranchises, setActiveFranchises] = useState([]);
    const [franchiseTagsUsers, setFranchiseTagsUsers] = useState([]); // For tag assignment
    const [tagRequests, setTagRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [franchiseSummary, setFranchiseSummary] = useState({
        total: 0,
        tag: 0,
        restaurant: 0
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    });

    // Direct creation states
    const [showCreateFranchiseModal, setShowCreateFranchiseModal] = useState(false);
    const [showAssignTagsModal, setShowAssignTagsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [franchiseForm, setFranchiseForm] = useState({
        name: '',
        email: '',
        mobile: '',
        business_name: '',
        address: '',
        role: 'franchise_tags' // Default to franchise_tags
    });
    const [tagAssignForm, setTagAssignForm] = useState({
        franchiseId: '',
        carCount: 0,
        bikeCount: 0
    });

    // Search states for active franchises
    const [activeSearch, setActiveSearch] = useState({
        area: '',
        contact: ''
    });

    useEffect(() => {
        fetchData();
    }, [subTab, pagination.page, pagination.limit, activeSearch.area, activeSearch.contact]);

    useEffect(() => {
        fetchFranchiseSummary();
    }, []);

    const fetchFranchiseSummary = async () => {
        try {
            const res = await api.get('/admin/dashboard');
            if (res.data.success) {
                const d = res.data.data || {};
                setFranchiseSummary({
                    total: d.totalFranchises ?? 0,
                    tag: d.totalTagFranchises ?? 0,
                    restaurant: d.totalRestaurantFranchises ?? 0
                });
            }
        } catch (err) {
            console.error('Error fetching franchise summary:', err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (subTab === 'pending') {
                const [franchiseRes, tagRes] = await Promise.all([
                    api.get('/franchise/requests'),
                    api.get('/admin/tags/requests')
                ]);

                let combined = [];
                if (franchiseRes.data.success) {
                    combined = franchiseRes.data.data
                        .filter(r => r.status === 'PENDING')
                        .map(r => ({ ...r, requestType: r.type || 'franchise' }));
                }
                if (tagRes.data.success) {
                    const tagData = Array.isArray(tagRes.data.data) ? tagRes.data.data : (tagRes.data.data.requests || []);
                    const pendingTags = tagData
                        .filter(t => t.status === 'PENDING')
                        .map(t => ({ ...t, requestType: 'tag_refill' }));
                    combined = [...combined, ...pendingTags];
                }

                combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setPendingRequests(combined);
            } else if (subTab === 'active') {
                const params = new URLSearchParams({
                    page: pagination.page,
                    limit: pagination.limit,
                    role: 'franchise,franchise_tags'
                });
                // Add search parameters - combine contact and area searches
                const searchTerms = [];
                if (activeSearch.contact) {
                    searchTerms.push(activeSearch.contact);
                }
                if (activeSearch.area) {
                    searchTerms.push(activeSearch.area);
                }
                if (searchTerms.length > 0) {
                    // For multiple search terms, we'll search for any match
                    // Backend will search across name, email, mobile, and address
                    params.append('search', searchTerms.join(' '));
                }
                const res = await api.get(`/admin/users?${params}`);
                if (res.data.success) {
                    let franchises = res.data.data.users;
                    // Additional client-side filtering if both contact and area are specified
                    // This ensures we get results that match both criteria
                    if (activeSearch.contact && activeSearch.area) {
                        const contactLower = activeSearch.contact.toLowerCase();
                        const areaLower = activeSearch.area.toLowerCase();
                        franchises = franchises.filter(f => {
                            const matchesContact = !activeSearch.contact || 
                                (f.mobile && f.mobile.includes(activeSearch.contact)) ||
                                (f.name && f.name.toLowerCase().includes(contactLower)) ||
                                (f.email && f.email.toLowerCase().includes(contactLower));
                            const matchesArea = !activeSearch.area || 
                                (f.address && f.address.toLowerCase().includes(areaLower));
                            return matchesContact && matchesArea;
                        });
                    }
                    setActiveFranchises(franchises);
                    setPagination(res.data.data.pagination);
                }
            } else if (subTab === 'tag-requests') {
                const params = new URLSearchParams({
                    page: pagination.page,
                    limit: pagination.limit
                });
                const res = await api.get(`/admin/tags/requests?${params}`);
                if (res.data.success) {
                    const tagData = Array.isArray(res.data.data) ? res.data.data : (res.data.data.requests || []);
                    // Focus on APPROVED ones for assignment
                    setTagRequests(tagData);
                    if (res.data.data.pagination) setPagination(res.data.data.pagination);
                }
            }
        } catch (err) {
            console.error('Error fetching franchise data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApproveRequest = async (id) => {
        if (!window.confirm('Approve this franchise request?')) return;
        try {
            const res = await api.post(`/franchise/request/${id}/approve`);
            if (res.data.success) {
                alert('Franchise approved successfully! The franchise will now appear in the Active Franchises list.');
                fetchData();
            } else {
                alert('Approval failed: ' + (res.data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Approval failed: ' + (err.response?.data?.message || err.message || 'Unknown error'));
        }
    };

    const handleApproveTags = async (id) => {
        if (!window.confirm('Approve this tag request?')) return;
        try {
            const res = await api.post(`/admin/tags/request/${id}/approve`);
            if (res.data.success) {
                alert(res.data.message || 'Tag request approved');
            }
            fetchData();
        } catch (err) {
            console.error('Approval failed:', err);
        }
    };

    const handleRejectRequest = async (id) => {
        if (!window.confirm('Reject this franchise request?')) return;
        try {
            await api.post(`/franchise/request/${id}/reject`);
            fetchData();
        } catch (err) {
            alert('Rejection failed');
        }
    };

    const handleRejectTags = async (id) => {
        if (!window.confirm('Reject this tag refill request?')) return;
        try {
            await api.post(`/admin/tags/request/${id}/reject`);
            fetchData();
        } catch (err) {
            alert('Rejection failed');
        }
    };

    const openRequestDetails = (req) => {
        setSelectedRequest(req);
    };

    const closeRequestDetails = () => {
        setSelectedRequest(null);
    };

    const handleAssignTags = async (franchiseId) => {
        const range = window.prompt('Enter tag ID range (e.g. 100-200) or comma separated IDs (e.g. 1,2,3)');
        if (!range) return;

        let tagIds = [];
        try {
            if (range.includes('-')) {
                const [start, end] = range.split('-').map(s => parseInt(s.trim()));
                if (isNaN(start) || isNaN(end)) throw new Error('Invalid range');
                for (let i = start; i <= end; i++) tagIds.push(i);
            } else {
                tagIds = range.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
            }

            if (tagIds.length === 0) return alert('No valid IDs provided');

            const res = await api.post('/admin/qrcodes/assign', { tagIds, ownerId: franchiseId });
            if (res.data.success) {
                alert(`Successfully assigned ${res.data.data.assignedCount} tags!`);
            }
        } catch (err) {
            alert('Tag assignment failed: ' + (err.message || 'Unknown error'));
        }
    };

    // Direct franchise creation
    const handleCreateFranchise = async () => {
        try {
            if (!franchiseForm.name || !franchiseForm.email || !franchiseForm.business_name) {
                return alert('Please fill in all required fields (Name, Email, Business Name)');
            }

            const res = await api.post('/admin/users', {
                ...franchiseForm,
                password: Math.random().toString(36).slice(-8) // Generate random password
            });

            if (res.data.success) {
                const roleLabel = franchiseForm.role === 'franchise_tags' ? 'Tag Distributor' : 'Franchise';
                alert(`${roleLabel} created successfully! Password sent to email.`);
                setShowCreateFranchiseModal(false);
                setFranchiseForm({ name: '', email: '', mobile: '', business_name: '', address: '', role: 'franchise_tags' });
                fetchData();
                // Refresh franchise_tags list if that role was created
                if (franchiseForm.role === 'franchise_tags') {
                    setFranchiseTagsUsers([]);
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create franchise');
        }
    };

    // Direct tag generation and assignment
    const handleGenerateAndAssignTags = async () => {
        try {
            if (!tagAssignForm.franchiseId) {
                return alert('Please select a franchise');
            }

            const totalCount = parseInt(tagAssignForm.carCount) + parseInt(tagAssignForm.bikeCount);
            if (totalCount === 0) {
                return alert('Please enter at least one car or bike tag');
            }

            // Generate car tags
            if (tagAssignForm.carCount > 0) {
                const carRes = await api.post('/qrcodes/generate', {
                    count: parseInt(tagAssignForm.carCount),
                    is_car: true,
                    owner_id: parseInt(tagAssignForm.franchiseId)
                });
                if (!carRes.data.success) throw new Error('Failed to generate car tags');
            }

            // Generate bike tags
            if (tagAssignForm.bikeCount > 0) {
                const bikeRes = await api.post('/qrcodes/generate', {
                    count: parseInt(tagAssignForm.bikeCount),
                    is_car: false,
                    owner_id: parseInt(tagAssignForm.franchiseId)
                });
                if (!bikeRes.data.success) throw new Error('Failed to generate bike tags');
            }

            alert(`Successfully generated ${totalCount} tags!`);
            setShowAssignTagsModal(false);
            setTagAssignForm({ franchiseId: '', carCount: 0, bikeCount: 0 });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to generate and assign tags');
        }
    };

    return (
        <div className="admin-franchises">
            <header className="page-header">
                <div className="header-info">
                    <h2>Franchise Management</h2>
                    <p>Manage applications, active franchises and tag requests</p>
                </div>
                <div className="header-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button 
                        className="btn-secondary" 
                        onClick={() => {
                            const url = `${window.location.origin}/franchise`;
                            navigator.clipboard.writeText(url).then(() => {
                                alert('Franchise Form URL copied to clipboard: ' + url);
                            }).catch(err => {
                                console.error('Failed to copy: ', err);
                                alert('Failed to copy URL. The URL is: ' + url);
                            });
                        }}
                    >
                        Share Franchise Link
                    </button>
                    <button 
                        className="btn-secondary" 
                        onClick={() => {
                            const url = `${window.location.origin}/franchise?interest=tags`;
                            navigator.clipboard.writeText(url).then(() => {
                                alert('Tag Management Form URL copied to clipboard: ' + url);
                            }).catch(err => {
                                console.error('Failed to copy: ', err);
                                alert('Failed to copy URL. The URL is: ' + url);
                            });
                        }}
                    >
                        Share Tag Link
                    </button>
                    <button className="btn-primary" onClick={() => setShowCreateFranchiseModal(true)}>
                        + Create Franchise
                    </button>
                    <button className="btn-secondary" onClick={async () => {
                        try {
                            const res = await api.get('/admin/users?role=franchise_tags&limit=500');
                            if (res.data.success) {
                                setFranchiseTagsUsers(res.data.data.users);
                            }
                        } catch (err) {
                            console.error('Failed to load franchise_tags users:', err);
                        }
                        setShowAssignTagsModal(true);
                    }}>
                        + Assign Tags
                    </button>
                </div>
            </header>

            <div className="sub-tabs">
                <button
                    className={subTab === 'pending' ? 'active' : ''}
                    onClick={() => setSubTab('pending')}
                >
                    Applications ({pendingRequests.length})
                </button>
                <button
                    className={subTab === 'active' ? 'active' : ''}
                    onClick={() => { 
                        setSubTab('active'); 
                        setPagination({ page: 1, limit: pagination.limit, total: 0, pages: 1 });
                        setActiveSearch({ area: '', contact: '' });
                    }}
                >
                    Active Franchises ({franchiseSummary.total})
                </button>
                {/* <button
                    className={subTab === 'tag-requests' ? 'active' : ''}
                    onClick={() => { 
                        setSubTab('tag-requests'); 
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                >
                    Tag Assignments
                </button> */}
            </div>

            <div className="card">
                {/* Search Bar for Active Franchises */}
                {subTab === 'active' && (
                    <div style={{ 
                        padding: '1rem', 
                        borderBottom: '1px solid var(--border-color)',
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        alignItems: 'center'
                    }}>
                        <div style={{ width: '100%', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Tag Distributors: <strong style={{ color: 'var(--text-primary)' }}>{franchiseSummary.tag}</strong> | Restaurant Franchises: <strong style={{ color: 'var(--text-primary)' }}>{franchiseSummary.restaurant}</strong>
                        </div>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                Search by Contact Number
                            </label>
                            <input
                                type="text"
                                placeholder="Enter contact number..."
                                value={activeSearch.contact}
                                onChange={(e) => {
                                    setActiveSearch({ ...activeSearch, contact: e.target.value });
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-input)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                        </div>
                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                Search by Area/Address
                            </label>
                            <input
                                type="text"
                                placeholder="Enter area or address..."
                                value={activeSearch.area}
                                onChange={(e) => {
                                    setActiveSearch({ ...activeSearch, area: e.target.value });
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-input)',
                                    color: 'var(--text-primary)'
                                }}
                            />
                        </div>
                        {(activeSearch.contact || activeSearch.area) && (
                            <button
                                onClick={() => {
                                    setActiveSearch({ area: '', contact: '' });
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-card)',
                                    color: 'var(--text-primary)',
                                    cursor: 'pointer',
                                    marginTop: '1.75rem'
                                }}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}

                <div className="table-container desktop-only">
                    {loading ? (
                        <div className="text-center p-4">Loading...</div>
                    ) : subTab === 'pending' ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Business / Franchise</th>
                                    <th>Details</th>
                                    <th>Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingRequests.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center">No pending applications</td></tr>
                                ) : (
                                    pendingRequests.map(req => (
                                        <tr key={`${req.requestType}-${req.id}`}>
                                            <td>
                                                <span className={`badge badge-${req.requestType}`}>
                                                    {req.requestType === 'tag_refill' ? 'Tag Refill' : 
                                                     req.requestType === 'tags' ? 'Tag Application' : 
                                                     req.requestType === 'restaurant' ? 'Restaurant' : 'Franchise'}
                                                </span>
                                            </td>
                                            <td>
                                                <strong>{req.business_name}</strong>
                                                <div className="text-xs text-secondary">{req.name || req.franchise_name}</div>
                                            </td>
                                            <td>
                                                {req.requestType === 'tag_refill' ? (
                                                    <div className="text-xs">
                                                        🚲 {req.bike_count} | 🚗 {req.car_count}<br />
                                                        <span title={req.shipping_address}>📍 {req.shipping_address?.substring(0, 20)}...</span>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs">
                                                        📍 {req.location}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{formatDate(req.created_at)}</td>
                                            <td>
                                                <div className="admin-actions">
                                                    <button
                                                        className="btn-approve"
                                                        onClick={() => req.requestType === 'tag_refill' ? handleApproveTags(req.id) : handleApproveRequest(req.id)}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn-reject"
                                                        onClick={() => req.requestType === 'tag_refill' ? handleRejectTags(req.id) : handleRejectRequest(req.id)}
                                                    >
                                                        Reject
                                                    </button>
                                                    <button
                                                        className="btn-view"
                                                        onClick={() => openRequestDetails(req)}
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : subTab === 'active' ? (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Franchise</th>
                                    <th>Business</th>
                                    <th>Role</th>
                                    <th>Contact Number</th>
                                    <th>Mandal Name</th>
                                    <th>Area/Address</th>
                                    <th>Joined</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeFranchises.length === 0 ? (
                                    <tr><td colSpan="9" className="text-center">No active franchises found</td></tr>
                                ) : (
                                    activeFranchises.map(u => (
                                        <tr key={u.id}>
                                            <td>#{u.id}</td>
                                            <td><strong>{u.name}</strong></td>
                                            <td>{u.business_name || '-'}</td>
                                            <td>
                                                <span className={`badge badge-${u.role}`}>
                                                    {u.role === 'franchise_tags' ? 'Tag Distributor' : 'Restaurant'}
                                                </span>
                                            </td>
                                            <td>{u.mobile || '-'}</td>
                                            <td>{u.mandal_name || '-'}</td>
                                            <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.address || '-'}>
                                                {u.address || '-'}
                                            </td>
                                            <td>{formatDate(u.created_at)}</td>
                                            <td><button className="btn-view" onClick={() => navigate(`/admin/users?email=${u.email}`)}>View</button></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Franchise</th>
                                    <th>Counts</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tagRequests.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center">No tag requests</td></tr>
                                ) : (
                                    tagRequests.map(req => (
                                        <tr key={req.id}>
                                            <td>{req.business_name}</td>
                                            <td>
                                                <div>🚲 {req.bike_count}</div>
                                                <div>🚗 {req.car_count}</div>
                                            </td>
                                            <td className="text-sm">{req.shipping_address}</td>
                                            <td><span className={`badge ${req.status.toLowerCase()}`}>{req.status}</span></td>
                                            <td>
                                                <div className="admin-actions">
                                                     {req.status === 'PENDING' && (
                                                         <>
                                                            <button className="btn-approve" onClick={() => handleApproveTags(req.id)}>Approve</button>
                                                            <button className="btn-reject" onClick={() => handleRejectTags(req.id)}>Reject</button>
                                                         </>
                                                     )}
                                                    {req.status === 'APPROVED' && (
                                                        <button className="btn-assign" onClick={() => handleAssignTags(req.franchise_id)}>Assign Tags</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Mobile View: Cards */}
                <div className="mobile-only cards-list">
                    {loading ? (
                        <div className="text-center p-4">Loading...</div>
                    ) : (
                        <>
                            {subTab === 'pending' && (
                                pendingRequests.length === 0 ? (
                                    <div className="empty-state">No pending applications</div>
                                ) : (
                                    pendingRequests.map(req => (
                                        <div key={`${req.requestType}-${req.id}`} className="mobile-card">
                                            <div className="card-header-mobile">
                                                <span className={`badge badge-${req.requestType}`}>
                                                    {req.requestType === 'tag_refill' ? 'Tag Refill' : 
                                                     req.requestType === 'tags' ? 'Tag' : 
                                                     req.requestType === 'restaurant' ? 'Restaurant' : 'Franchise'}
                                                </span>
                                                <span className="date-mobile">{formatDate(req.created_at)}</span>
                                            </div>
                                            <div className="card-body-mobile">
                                                <h3>{req.business_name}</h3>
                                                <p className="owner-mobile">{req.name || req.franchise_name}</p>
                                                <div className="details-mobile">
                                                    {req.requestType === 'tag_refill' ? (
                                                        <>
                                                            <div className="counts">🚲 {req.bike_count} | 🚗 {req.car_count}</div>
                                                            <div className="address">📍 {req.shipping_address}</div>
                                                        </>
                                                    ) : (
                                                        <div className="location">📍 {req.location}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="card-footer-mobile">
                                                <div className="admin-actions-mobile">
                                                    <button
                                                        className="btn-approve full-width"
                                                        onClick={() => req.requestType === 'tag_refill' ? handleApproveTags(req.id) : handleApproveRequest(req.id)}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn-reject full-width"
                                                        onClick={() => req.requestType === 'tag_refill' ? handleRejectTags(req.id) : handleRejectRequest(req.id)}
                                                    >
                                                        Reject
                                                    </button>
                                                    <button
                                                        className="btn-view full-width"
                                                        onClick={() => openRequestDetails(req)}
                                                    >
                                                        View Details
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )
                            )}

                            {subTab === 'active' && (
                                <>
                                    {/* Mobile Search Bar */}
                                    <div style={{ 
                                        padding: '1rem', 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem'
                                    }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                                Search by Contact Number
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter contact number..."
                                                value={activeSearch.contact}
                                                onChange={(e) => {
                                                    setActiveSearch({ ...activeSearch, contact: e.target.value });
                                                    setPagination(prev => ({ ...prev, page: 1 }));
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    borderRadius: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    background: 'var(--bg-input)',
                                                    color: 'var(--text-primary)'
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                                Search by Area/Address
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter area or address..."
                                                value={activeSearch.area}
                                                onChange={(e) => {
                                                    setActiveSearch({ ...activeSearch, area: e.target.value });
                                                    setPagination(prev => ({ ...prev, page: 1 }));
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    borderRadius: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    background: 'var(--bg-input)',
                                                    color: 'var(--text-primary)'
                                                }}
                                            />
                                        </div>
                                        {(activeSearch.contact || activeSearch.area) && (
                                            <button
                                                onClick={() => {
                                                    setActiveSearch({ area: '', contact: '' });
                                                    setPagination(prev => ({ ...prev, page: 1 }));
                                                }}
                                                style={{
                                                    padding: '0.75rem',
                                                    borderRadius: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    background: 'var(--bg-card)',
                                                    color: 'var(--text-primary)',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Clear Search
                                            </button>
                                        )}
                                    </div>
                                    {activeFranchises.length === 0 ? (
                                        <div className="empty-state">No active franchises found</div>
                                    ) : (
                                        activeFranchises.map(u => (
                                            <div key={u.id} className="mobile-card">
                                                <div className="card-header-mobile">
                                                    <span className={`badge badge-${u.role}`}>
                                                        {u.role === 'franchise_tags' ? 'Tag Distributor' : 'Restaurant'}
                                                    </span>
                                                    <span className="date-mobile">{formatDate(u.created_at)}</span>
                                                </div>
                                                <div className="card-body-mobile">
                                                    <h3>{u.business_name || u.name}</h3>
                                                    <p className="owner-mobile">{u.name}</p>
                                                    <p className="contact-mobile">📞 {u.mobile || 'No contact'}</p>
                                                    {u.mandal_name && (
                                                        <p className="contact-mobile" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                                            🏘️ Mandal: {u.mandal_name}
                                                        </p>
                                                    )}
                                                    <p className="contact-mobile" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                                        📍 {u.address || 'No address'}
                                                    </p>
                                                </div>
                                                <div className="card-footer-mobile">
                                                    <button className="btn-view full-width" onClick={() => navigate(`/admin/users?email=${u.email}`)}>View Profile</button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </>
                            )}

                            {subTab === 'tag-requests' && (
                                tagRequests.length === 0 ? (
                                    <div className="empty-state">No tag distributions</div>
                                ) : (
                                    tagRequests.map(req => (
                                        <div key={req.id} className="mobile-card">
                                            <div className="card-header-mobile">
                                                <span className={`badge ${req.status.toLowerCase()}`}>{req.status}</span>
                                                <span className="date-mobile">{formatDate(req.created_at)}</span>
                                            </div>
                                            <div className="card-body-mobile">
                                                <h3>{req.business_name}</h3>
                                                <div className="counts">🚲 {req.bike_count} | 🚗 {req.car_count}</div>
                                                <p className="address">📍 {req.shipping_address}</p>
                                            </div>
                                            <div className="card-footer-mobile">
                                                <div className="admin-actions-mobile">
                                                     {req.status === 'PENDING' && (
                                                         <>
                                                            <button className="btn-approve full-width" onClick={() => handleApproveTags(req.id)}>Approve</button>
                                                            <button className="btn-reject full-width" onClick={() => handleRejectTags(req.id)}>Reject</button>
                                                         </>
                                                     )}
                                                    {req.status === 'APPROVED' && (
                                                        <button className="btn-assign full-width" onClick={() => handleAssignTags(req.franchise_id)}>Assign Tags</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )
                            )}
                        </>
                    )}
                </div>
            </div>


            {subTab !== 'pending' && pagination.pages > 1 && (
                <div className="pagination">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Items:</label>
                        <select
                            value={pagination.limit}
                            onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                            style={{
                                padding: '0.5rem',
                                borderRadius: '0.5rem',
                                background: 'var(--bg-input)',
                                border: '1px solid var(--border-color)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                    </div>
                    <button
                        disabled={pagination.page === 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                        Previous
                    </button>
                    <span>Page {pagination.page} of {pagination.pages}</span>
                    <button
                        disabled={pagination.page === pagination.pages}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                        Next
                    </button>
                </div>
            )}

            {selectedRequest && (
                <div className="modal-overlay" onClick={closeRequestDetails}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
                        <h3 style={{ marginBottom: '1rem' }}>Application Details</h3>
                        <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '65vh', overflowY: 'auto' }}>
                            <div><strong>Type:</strong> {selectedRequest.requestType === 'tag_refill' ? 'Tag Refill' : selectedRequest.type === 'tags' ? 'Tag Distributor' : 'Restaurant Franchise'}</div>
                            <div><strong>Status:</strong> {selectedRequest.status || 'PENDING'}</div>
                            <div><strong>Name:</strong> {selectedRequest.name || selectedRequest.franchise_name || '-'}</div>
                            <div><strong>Business Name:</strong> {selectedRequest.business_name || '-'}</div>
                            <div><strong>Email:</strong> {selectedRequest.email || '-'}</div>
                            <div><strong>Phone:</strong> {selectedRequest.phone || '-'}</div>
                            <div><strong>Address:</strong> {selectedRequest.address || selectedRequest.shipping_address || '-'}</div>
                            <div><strong>Village:</strong> {selectedRequest.village_name || '-'}</div>
                            <div><strong>Mandal:</strong> {selectedRequest.mandal_name || '-'}</div>
                            <div><strong>City:</strong> {selectedRequest.city_name || '-'}</div>
                            <div><strong>State:</strong> {selectedRequest.state || '-'}</div>
                            <div><strong>Pincode:</strong> {selectedRequest.pincode || '-'}</div>
                            <div><strong>Location:</strong> {selectedRequest.location || '-'}</div>
                            {selectedRequest.requestType === 'tag_refill' && (
                                <div><strong>Requested Tags:</strong> Bike {selectedRequest.bike_count || 0}, Car {selectedRequest.car_count || 0}</div>
                            )}
                            <div><strong>Message:</strong> {selectedRequest.message || '-'}</div>
                            <div><strong>Submitted At:</strong> {formatDate(selectedRequest.created_at)}</div>
                        </div>
                        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button className="btn-secondary" onClick={closeRequestDetails}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Franchise Modal */}
            {showCreateFranchiseModal && (
                <div className="modal-overlay" onClick={() => setShowCreateFranchiseModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <h3 style={{ marginBottom: '1.5rem' }}>Create New Franchise</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={franchiseForm.name}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, name: e.target.value })}
                                    placeholder="Franchise Owner Name"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email *</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={franchiseForm.email}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, email: e.target.value })}
                                    placeholder="franchise@example.com"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Mobile</label>
                                <input
                                    type="tel"
                                    className="form-input"
                                    value={franchiseForm.mobile}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, mobile: e.target.value })}
                                    placeholder="10-digit mobile number"
                                    maxLength={10}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Role / Type *</label>
                                <select
                                    className="form-input"
                                    value={franchiseForm.role}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, role: e.target.value })}
                                >
                                    <option value="franchise_tags">Tag Distributor (franchise_tags)</option>
                                    <option value="franchise">Business Franchise Owner (franchise)</option>
                                </select>
                                <small style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                                    {franchiseForm.role === 'franchise_tags' 
                                        ? 'Can manage and distribute QR tags to customers' 
                                        : 'Restaurant/business owner with menu & ordering features'}
                                </small>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Business Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={franchiseForm.business_name}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, business_name: e.target.value })}
                                    placeholder="Business Name"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Address</label>
                                <textarea
                                    className="form-input"
                                    rows={3}
                                    value={franchiseForm.address}
                                    onChange={(e) => setFranchiseForm({ ...franchiseForm, address: e.target.value })}
                                    placeholder="Business Address"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <button className="btn-secondary" onClick={() => setShowCreateFranchiseModal(false)} style={{ flex: 1 }}>
                                    Cancel
                                </button>
                                <button className="btn-primary" onClick={handleCreateFranchise} style={{ flex: 1 }}>
                                    Create Franchise
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Tags Modal */}
            {showAssignTagsModal && (
                <div className="modal-overlay" onClick={() => setShowAssignTagsModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <h3 style={{ marginBottom: '1.5rem' }}>Generate & Assign Tags</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Select Franchise (Tag Distributor) *</label>
                                <select
                                    className="form-input"
                                    value={tagAssignForm.franchiseId}
                                    onChange={(e) => setTagAssignForm({ ...tagAssignForm, franchiseId: e.target.value })}
                                >
                                    <option value="">-- Select Franchise --</option>
                                    {franchiseTagsUsers.map(f => (
                                        <option key={f.id} value={f.id}>
                                            #{f.id} - {f.business_name || f.name} ({f.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🚗 Car Tags</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    min="0"
                                    value={tagAssignForm.carCount}
                                    onChange={(e) => setTagAssignForm({ ...tagAssignForm, carCount: e.target.value })}
                                    placeholder="Number of car tags"
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🚲 Bike Tags</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    min="0"
                                    value={tagAssignForm.bikeCount}
                                    onChange={(e) => setTagAssignForm({ ...tagAssignForm, bikeCount: e.target.value })}
                                    placeholder="Number of bike tags"
                                />
                            </div>
                            <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', fontSize: '0.9rem' }}>
                                <strong>Total Tags:</strong> {parseInt(tagAssignForm.carCount || 0) + parseInt(tagAssignForm.bikeCount || 0)}
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <button className="btn-secondary" onClick={() => setShowAssignTagsModal(false)} style={{ flex: 1 }}>
                                    Cancel
                                </button>
                                <button className="btn-primary" onClick={handleGenerateAndAssignTags} style={{ flex: 1 }}>
                                    Generate & Assign
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminFranchises;
